import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Download, Search, BookOpen } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { iconForCategory } from "@/constants/resourceCategories";

/**
 * Resources — searchable library of contracts, scripts, and templates.
 * Items load from /api/resources (backend-managed). Categories are fixed in code.
 */

function buildItemUrl(item) {
  if (item.file_id) return fileUrl(item.file_id);
  return item.url;
}

function fileTypeLabel(url) {
  const lower = (url || "").toLowerCase();
  if (lower.includes(".pdf")) return "Open / download PDF";
  if (lower.includes(".docx") || lower.includes(".doc")) return "Download document";
  return "Open resource";
}

export default function Resources() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/resource-categories"),
      api.get("/resources"),
    ])
      .then(([catRes, itemsRes]) => {
        setCategories(catRes.data);
        setItems(itemsRes.data);
        setActiveCat((curr) => curr ?? (catRes.data[0]?.id || null));
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load resources"))
      .finally(() => setLoading(false));
  }, []);

  // Group items by category id, applying server-side order.
  const grouped = useMemo(() => {
    const map = {};
    for (const c of categories) map[c.id] = [];
    for (const it of items) if (map[it.category_id]) map[it.category_id].push(it);
    return map;
  }, [items, categories]);

  const activeCategory = categories.find((c) => c.id === activeCat);
  const activeItems = grouped[activeCat] || [];

  const allItemsWithCategory = useMemo(() =>
    items.map((it) => {
      const cat = categories.find((c) => c.id === it.category_id);
      return { ...it, categoryTitle: cat?.title || it.category_id };
    }), [items, categories]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return allItemsWithCategory.filter(
      (i) => i.title.toLowerCase().includes(q)
        || (i.description || "").toLowerCase().includes(q)
        || (i.categoryTitle || "").toLowerCase().includes(q),
    );
  }, [query, allItemsWithCategory]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto" data-testid="resources-page">
        <div className="flex items-center justify-center py-20"><span className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="resources-page">
      <Header />

      <div className="relative mb-7">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search contracts, scripts, templates..."
          className="w-full pl-11 pr-4 py-3 rounded-2xl text-sm"
          style={{ background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text)" }}
          data-testid="resources-search-input"
        />
      </div>

      {filtered !== null ? (
        <section data-testid="resources-search-results">
          <div className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--text-muted)" }}>
            {filtered.length} {filtered.length === 1 ? "result" : "results"} for &quot;{query}&quot;
          </div>
          {filtered.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filtered.map((item) => <ResourceCard key={item.id} item={item} showCategory />)}
            </div>
          )}
        </section>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 items-start">
          <CategoryList categories={categories} grouped={grouped} activeId={activeCat} onSelect={setActiveCat} />
          <CategoryItems category={activeCategory} items={activeItems} />
        </div>
      )}
    </div>
  );
}

function Header() {
  return (
    <div className="flex items-start gap-4 mb-7">
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
        style={{ background: "var(--lime-glow)", color: "var(--lime)" }}
      >
        <BookOpen size={22} strokeWidth={1.6} />
      </div>
      <div>
        <div className="section-tag mb-2"><span className="dot" /> Toolkit</div>
        <h1 className="h-display text-3xl sm:text-4xl">Resources</h1>
        <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--text-muted)" }}>
          Every contract, script, and template you need to run your business. Tap any card to open the file.
        </p>
      </div>
    </div>
  );
}

function CategoryList({ categories, grouped, activeId, onSelect }) {
  return (
    <div className="surface-card p-4">
      <div className="flex items-center gap-2 mb-4 px-1">
        <BookOpen size={14} style={{ color: "var(--lime)" }} />
        <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text)" }}>
          Library
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {categories.map((c) => {
          const isActive = c.id === activeId;
          const Icon = iconForCategory(c.icon);
          const count = grouped[c.id]?.length || 0;
          return (
            <button
              key={c.id}
              onClick={() => onSelect(c.id)}
              className="flex items-start gap-3 p-3 rounded-2xl text-left transition"
              style={{
                background: isActive ? "var(--lime-glow)" : "var(--surface2)",
                border: isActive ? "1px solid var(--lime)" : "1px solid var(--border)",
              }}
              data-testid={`resources-cat-${c.id}`}
            >
              <Icon size={16} className="mt-0.5 shrink-0" style={{ color: isActive ? "var(--lime)" : "var(--text-muted)" }} />
              <div className="min-w-0">
                <div className="text-sm font-semibold whitespace-nowrap" style={{ color: isActive ? "var(--lime)" : "var(--text)" }}>
                  {c.title}
                </div>
                <div className="font-mono text-[10px] mt-0.5" style={{ color: "var(--text-dim)" }}>
                  {count} {count === 1 ? "item" : "items"}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CategoryItems({ category, items }) {
  if (!category) return null;
  return (
    <div>
      <h2 className="h-display text-2xl mb-1">{category.title}</h2>
      <p className="text-sm mb-5 max-w-2xl" style={{ color: "var(--text-muted)" }}>{category.description}</p>
      {items.length === 0 ? (
        <div className="surface-card p-10 text-center">
          <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--surface2)", color: "var(--text-muted)" }}>
            <BookOpen size={18} />
          </div>
          <h3 className="font-semibold mb-1">Nothing here yet</h3>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            Salvador hasn&apos;t added a resource to this category yet. Check back soon.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((item) => <ResourceCard key={item.id} item={item} />)}
        </div>
      )}
    </div>
  );
}

function ResourceCard({ item, showCategory }) {
  const href = buildItemUrl(item);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="surface-card p-5 transition hover:opacity-95 hover:-translate-y-0.5 group flex flex-col"
      data-testid={`resource-card-${(item.id || item.title).toString().replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--surface2)", color: "var(--text-muted)" }}>
          <FileTextIcon />
        </div>
        <ExternalLink size={14} className="opacity-0 group-hover:opacity-100 transition" style={{ color: "var(--lime)" }} />
      </div>
      <h3 className="text-base font-semibold mb-1.5">{item.title}</h3>
      <p className="text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>{item.description}</p>
      {showCategory && (
        <div className="mt-3 font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>{item.categoryTitle}</div>
      )}
      <div className="mt-auto pt-4 flex items-center gap-2 font-mono text-[11px]" style={{ color: "var(--lime)" }}>
        <Download size={11} /> {fileTypeLabel(href)}
      </div>
    </a>
  );
}

function FileTextIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function EmptyState() {
  return (
    <div className="surface-card p-10 text-center">
      <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--surface2)", color: "var(--text-muted)" }}>
        <Search size={18} />
      </div>
      <h3 className="font-semibold mb-1">Nothing matches</h3>
      <p className="text-sm" style={{ color: "var(--text-muted)" }}>Try a different keyword or clear the search to browse by category.</p>
    </div>
  );
}
